import React, { useState } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Header } from '@/components/Header';
import BottomNav from '@/components/BottomNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import {
  HeartHandshake,
  ShieldCheck,
  GraduationCap,
  Users,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Mail,
  FileText,
  Lightbulb,
} from 'lucide-react';
import { AutoTranslate } from '@/components/AutoTranslate';

const OUTCOMES = [
  { icon: HeartHandshake, label: 'Fewer misunderstandings', sub: 'Clearer language at home, school, work, and the community.' },
  { icon: ShieldCheck, label: 'Reduced anxiety', sub: 'Less ambiguity load — fewer shutdowns from confusing social cues.' },
  { icon: Sparkles, label: 'Stronger self-advocacy', sub: 'Plain-language phrases for "what did they actually mean?"' },
  { icon: GraduationCap, label: 'Smoother classrooms & workplaces', sub: 'Practical scripts clients can use between sessions.' },
];

const PACKAGES = [
  {
    id: 'A',
    name: 'Community Resource Partner',
    tag: 'Lowest lift',
    price: 'Free',
    color: 'from-emerald-500 to-teal-500',
    youGet: ['Short "how to use the app" blurb', 'Printable flyer + graphic', 'Trackable referral link'],
    youDo: ['Add it to your resource list / newsletter / welcome packet'],
    optional: 'Optional perk: VIP trial code or member discount.',
    why: 'Easy "yes." No meetings required.',
  },
  {
    id: 'B',
    name: 'Program Add-On',
    tag: 'Moderate lift',
    price: 'Free',
    color: 'from-cyan-500 to-blue-500',
    featured: true,
    youGet: ['30-min virtual intro session (live or recorded)', '"Phrase of the week" handout template', 'Branded conversation starters'],
    youDo: ['Offer it to families/clients as an optional support tool'],
    why: 'Adds structure so staff feel confident recommending it.',
  },
  {
    id: 'C',
    name: 'Co-Branded Pilot',
    tag: 'Highest value',
    price: "Let's talk",
    color: 'from-purple-500 to-fuchsia-500',
    youGet: ['4–6 week pilot plan (workplace, school, sarcasm, self-advocacy themes)', 'Feedback form + outcomes report template', 'Co-branded materials'],
    youDo: ['Recruit 10–30 participants and collect feedback'],
    why: 'Creates outcomes, testimonials, and a case study you can reuse.',
  },
];

const TRUST = [
  { icon: ShieldCheck, label: 'Privacy-first',
    body: 'No data sold. Phrases viewed, streaks, and goals stay tied to the user. Children/teen accounts get extra protections in our Privacy Policy.' },
  { icon: FileText, label: 'Not medical advice',
    body: 'ClearCommunicationApp is a supportive language tool. It does not diagnose, treat, or replace therapy, speech-language pathology, or medical care.' },
  { icon: Users, label: 'Built with neurodivergence in mind',
    body: 'Created and maintained by a neurodivergent solo parent of four neurodivergent children. We design with the people we serve.' },
];

const ForOrganizationsPage = () => {
  const [form, setForm] = useState({
    org_name: '',
    contact_name: '',
    email: '',
    role: '',
    org_type: '',
    package: 'unsure',
    audience_size: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.org_name || !form.contact_name || !form.email || !form.message) {
      toast.error('Please fill in the required fields.');
      return;
    }
    setSubmitting(true);
    const { error } = await apiFetch('/api/partners/inquiry', { method: 'POST', body: form });
    setSubmitting(false);
    if (error) {
      toast.error(typeof error === 'string' ? error : 'Could not submit. Please email partners@clearcommunicationapp.com.');
      return;
    }
    setSubmitted(true);
    toast.success('Thank you! We will be in touch within 3 business days.');
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="for-organizations-page">
      <Helmet>
        <title>For Organizations · ClearCommunicationApp</title>
        <meta name="description" content="Partner with ClearCommunicationApp to give the families, clients, and students you serve a supportive tool for decoding implied meaning, reducing ambiguity, and building self-advocacy." />
        <link rel="canonical" href="https://clearcommunicationapp.com/for-organizations" />
        <meta property="og:title" content="For Organizations · ClearCommunicationApp" />
        <meta property="og:description" content="Three ways to partner with us — from a quick resource share to a co-branded pilot — to help neurodivergent people and language learners communicate with more confidence." />
        <meta property="og:url" content="https://clearcommunicationapp.com/for-organizations" />
        <meta property="og:image" content="https://clearcommunicationapp.com/icon-pixel-512.png" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Header />

      <main className="container max-w-4xl mx-auto px-4 py-8 space-y-10 sm:space-y-12 overflow-x-hidden">
        <AutoTranslate>
        {/* Hero */}
        <section className="text-center space-y-4" data-testid="for-org-hero">
          <Badge className="bg-[#00E5CC]/10 text-[#00A896] border-[#00E5CC]/30 hover:bg-[#00E5CC]/20">
            For Organizations
          </Badge>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight break-words">
            A supportive communication tool
            <span className="block mt-2 bg-gradient-to-r from-[#00E5CC] to-purple-600 bg-clip-text text-transparent">
              for the families & clients you serve.
            </span>
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed break-words">
            ClearCommunicationApp helps neurodivergent people and language learners decode implied meaning —
            idioms, sarcasm, workplace phrases, regional and generational slang — so they can communicate
            with more confidence and fewer misunderstandings.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 text-xs">
            <Badge variant="secondary" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 whitespace-normal text-center">3,400+ phrases</Badge>
            <Badge variant="secondary" className="border-purple-500/30 bg-purple-500/10 text-purple-700 whitespace-normal text-center">EN · ES bilingual</Badge>
            <Badge variant="secondary" className="border-blue-500/30 bg-blue-500/10 text-blue-700 whitespace-normal text-center">Built with neurodivergence in mind</Badge>
            <Badge variant="secondary" className="border-orange-500/30 bg-orange-500/10 text-orange-700 whitespace-normal text-center">Web · App Stores · Cross-platform</Badge>
          </div>
          <div className="pt-2 flex flex-wrap gap-2">
            <Button
              asChild
              size="lg"
              className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] dark:text-[#0d1f2d]"
              data-testid="for-org-cta-contact"
            >
              <a href="#contact">
                Contact for partnerships <ArrowRight className="h-4 w-4 ml-2" />
              </a>
            </Button>
            {/* Press kit shortcut — same audience as journalist outreach
                often overlaps with organizational partners (admin/HR/PR
                teams). Linking the press kit here saves them a click. */}
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-[#00E5CC] text-[#0d1f2d] dark:text-[#00E5CC] hover:bg-[#00E5CC]/10"
              data-testid="for-org-press-kit-link"
            >
              <a href="/press">
                <FileText className="h-4 w-4 mr-2" />
                Press Kit
              </a>
            </Button>
          </div>
        </section>

        {/* Outcomes */}
        <section className="space-y-4" data-testid="for-org-outcomes">
          <h2 className="text-xl sm:text-2xl font-bold break-words">What improves for the people you serve</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {OUTCOMES.map((o) => {
              const Icon = o.icon;
              return (
                <Card key={o.label} className="hover:shadow-md transition-shadow">
                  <CardHeader className="flex flex-row items-start gap-3 pb-3">
                    <div className="shrink-0 w-10 h-10 rounded-lg bg-[#00E5CC]/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[#00A896]" />
                    </div>
                    <div>
                      <CardTitle className="text-base leading-tight">{o.label}</CardTitle>
                      <CardDescription className="mt-1 leading-relaxed">{o.sub}</CardDescription>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Why partners pick this */}
        <section className="mt-2 sm:mt-4 rounded-2xl bg-gradient-to-br from-purple-500/5 to-[#00E5CC]/5 border border-border p-6 sm:p-8 space-y-3">
          <div className="flex items-center gap-2 text-sm uppercase tracking-wider text-muted-foreground">
            <Lightbulb className="h-4 w-4" /> Why this fits autism orgs, schools, and therapy practices
          </div>
          <ul className="space-y-2 text-sm sm:text-base">
            {[
              'Supports self-advocacy — "what did they actually mean?"',
              'Reduces social ambiguity load that drives anxiety and shutdowns.',
              'Practical and repeatable — a tool clients use between sessions.',
              'Complements (not replaces) therapy, coaching, and SLP work.',
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 text-[#00A896] shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Packages */}
        <section className="space-y-4" data-testid="for-org-packages">
          <h2 className="text-xl sm:text-2xl font-bold break-words">Three ways to partner</h2>
          <p className="text-sm text-muted-foreground">
            Different orgs have different bandwidth — pick the lightest path that's still useful.
          </p>
          <div className="grid lg:grid-cols-3 gap-4">
            {PACKAGES.map((p) => (
              <Card
                key={p.id}
                data-testid={`partner-package-${p.id}`}
                className={`relative overflow-hidden ${p.featured ? 'ring-2 ring-[#00E5CC] shadow-md' : ''}`}
              >
                <div className={`h-1.5 bg-gradient-to-r ${p.color}`} />
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">Package {p.id}</Badge>
                    <span className="text-xs text-muted-foreground">{p.tag}</span>
                  </div>
                  <CardTitle className="text-lg leading-tight mt-2">{p.name}</CardTitle>
                  <CardDescription className="text-xs mt-1">{p.price}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold mb-1">You get:</p>
                    <ul className="space-y-1">
                      {p.youGet.map((g) => (
                        <li key={g} className="flex items-start gap-1.5">
                          <CheckCircle2 className="h-4 w-4 text-[#00A896] shrink-0 mt-0.5" />
                          <span>{g}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-1">You do:</p>
                    <ul className="space-y-1 text-muted-foreground">
                      {p.youDo.map((d) => <li key={d}>• {d}</li>)}
                    </ul>
                  </div>
                  {p.optional && <p className="text-xs italic text-muted-foreground">{p.optional}</p>}
                  <p className="text-xs text-foreground/80 border-t pt-2">{p.why}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Trust & safety */}
        <section className="space-y-4" data-testid="for-org-trust">
          <h2 className="text-xl sm:text-2xl font-bold break-words">Trust & safety</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {TRUST.map((t) => {
              const Icon = t.icon;
              return (
                <Card key={t.label} className="bg-muted/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Icon className="h-4 w-4 text-purple-600" /> {t.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground leading-relaxed">
                    {t.body}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Read the full <a className="underline" href="/privacy">Privacy Policy</a>.
          </p>
        </section>

        {/* Contact form */}
        <section
          id="contact"
          className="space-y-4 scroll-mt-20"
          data-testid="for-org-contact-section"
        >
          <h2 className="text-xl sm:text-2xl font-bold break-words">Tell us about your organization</h2>
          {submitted ? (
            <Card className="border-[#00E5CC]/40 bg-[#00E5CC]/5">
              <CardContent className="p-6 text-center space-y-2">
                <CheckCircle2 className="h-10 w-10 text-[#00A896] mx-auto" />
                <p className="font-semibold">Thanks — your inquiry is in.</p>
                <p className="text-sm text-muted-foreground">
                  We will reply within 3 business days. If urgent, email partners@clearcommunicationapp.com.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6">
                <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-4" data-testid="partner-form">
                  <div className="sm:col-span-2">
                    <Label htmlFor="org_name">Organization name *</Label>
                    <Input id="org_name" value={form.org_name} onChange={setField('org_name')} required data-testid="partner-org-name" />
                  </div>
                  <div>
                    <Label htmlFor="contact_name">Your name *</Label>
                    <Input id="contact_name" value={form.contact_name} onChange={setField('contact_name')} required data-testid="partner-contact-name" />
                  </div>
                  <div>
                    <Label htmlFor="role">Role / title</Label>
                    <Input id="role" value={form.role} onChange={setField('role')} placeholder="e.g., Program Director" data-testid="partner-role" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={form.email} onChange={setField('email')} required data-testid="partner-email" />
                  </div>
                  <div>
                    <Label htmlFor="org_type">Organization type</Label>
                    <Input id="org_type" value={form.org_type} onChange={setField('org_type')} placeholder="Autism org, school, therapy clinic, library, …" data-testid="partner-org-type" />
                  </div>
                  <div>
                    <Label htmlFor="package">Package interest</Label>
                    <Select value={form.package} onValueChange={(v) => setField('package')(v)}>
                      <SelectTrigger id="package" data-testid="partner-package-select">
                        <SelectValue placeholder="Pick one" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A — Community Resource Partner</SelectItem>
                        <SelectItem value="B">B — Program Add-On</SelectItem>
                        <SelectItem value="C">C — Co-Branded Pilot</SelectItem>
                        <SelectItem value="unsure">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="audience_size">Audience size</Label>
                    <Input id="audience_size" value={form.audience_size} onChange={setField('audience_size')} placeholder="e.g., 200 families, 30 staff, 1k newsletter" data-testid="partner-audience" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="message">What are you hoping to provide your audience? *</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={setField('message')}
                      rows={5}
                      placeholder="Tell us a little about who you serve and what would be most helpful…"
                      required
                      data-testid="partner-message"
                    />
                  </div>
                  <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]"
                      data-testid="partner-submit"
                    >
                      {submitting ? 'Sending…' : <>Send inquiry <Mail className="h-4 w-4 ml-2" /></>}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Or email <a className="underline" href="mailto:partners@clearcommunicationapp.com">partners@clearcommunicationapp.com</a>.
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </section>
        </AutoTranslate>
      </main>

      <BottomNav />
    </div>
  );
};

export default ForOrganizationsPage;
